import React from 'react';
import {
  Sparkles, CheckCircle2, Target, RefreshCw, Save, Printer, LayoutList, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function SchemeTab({
  schemeForm, setSchemeForm, schemeLoading, generateScheme,
  generatedScheme, saveScheme, printScheme, shareToAcademic, sharingToAcademic,
  getSubjectOptions, getYearOptions, shareLabel = 'Share to Academic'
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="scheme-tab-content">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutList className="h-5 w-5" />
            Term Details
          </CardTitle>
          <CardDescription>Configure your scheme of work</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Select
              value={schemeForm.subject}
              onValueChange={(value) => setSchemeForm({...schemeForm, subject: value})}
            >
              <SelectTrigger data-testid="scheme-select-subject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getSubjectOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Year Level</Label>
              <Select
                value={String(schemeForm.year_level)}
                onValueChange={(value) => setSchemeForm({...schemeForm, year_level: parseInt(value)})}
              >
                <SelectTrigger data-testid="scheme-select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Term</Label>
              <Select
                value={String(schemeForm.term)}
                onValueChange={(value) => setSchemeForm({...schemeForm, term: parseInt(value)})}
              >
                <SelectTrigger data-testid="scheme-select-term">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Term 1</SelectItem>
                  <SelectItem value="2">Term 2</SelectItem>
                  <SelectItem value="3">Term 3</SelectItem>
                  <SelectItem value="4">Term 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Number of Weeks</Label>
            <Select
              value={String(schemeForm.num_weeks)}
              onValueChange={(value) => setSchemeForm({...schemeForm, num_weeks: parseInt(value)})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[8,9,10,11,12].map(w => (
                  <SelectItem key={w} value={String(w)}>{w} weeks</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Strand / Unit (optional)</Label>
            <Input
              placeholder="e.g., Number & Operations"
              value={schemeForm.strand}
              onChange={(e) => setSchemeForm({...schemeForm, strand: e.target.value})}
              data-testid="scheme-input-strand"
            />
          </div>

          <Button
            className="w-full mt-4"
            onClick={generateScheme}
            disabled={schemeLoading}
            data-testid="btn-generate-scheme"
          >
            {schemeLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <LayoutList className="h-4 w-4 mr-2" />
                Generate Scheme
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <LayoutList className="h-5 w-5" />
              Term Scheme of Work
            </span>
            {generatedScheme && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={printScheme} data-testid="btn-print-scheme">
                  <Printer className="h-4 w-4 mr-1" />
                  Print
                </Button>
                <Button variant="outline" size="sm" onClick={saveScheme} data-testid="btn-save-scheme">
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
                {shareToAcademic && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={shareToAcademic}
                    disabled={sharingToAcademic}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-share-to-academic"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {sharingToAcademic ? 'Sharing...' : shareLabel}
                  </Button>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!generatedScheme ? (
            <div className="text-center py-12 text-muted-foreground">
              <LayoutList className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select subject, year, and term then click Generate to create your scheme of work</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse rounded-lg overflow-hidden shadow-sm" data-testid="scheme-table">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                    <th className="p-3 text-center w-16 font-semibold">Week</th>
                    <th className="p-3 text-left font-semibold" style={{width: '18%'}}>Topic</th>
                    <th className="p-3 text-left font-semibold" style={{width: '24%'}}>Objectives</th>
                    <th className="p-3 text-left font-semibold" style={{width: '24%'}}>Activities</th>
                    <th className="p-3 text-left font-semibold" style={{width: '14%'}}>Resources</th>
                    <th className="p-3 text-left font-semibold" style={{width: '14%'}}>Assessment</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedScheme.map((week, idx) => {
                    const colors = [
                      {bg: 'bg-blue-50', border: 'border-l-blue-500', badge: 'bg-blue-600'},
                      {bg: 'bg-emerald-50', border: 'border-l-emerald-500', badge: 'bg-emerald-600'},
                      {bg: 'bg-amber-50', border: 'border-l-amber-500', badge: 'bg-amber-600'},
                      {bg: 'bg-purple-50', border: 'border-l-purple-500', badge: 'bg-purple-600'},
                      {bg: 'bg-rose-50', border: 'border-l-rose-500', badge: 'bg-rose-600'},
                      {bg: 'bg-teal-50', border: 'border-l-teal-500', badge: 'bg-teal-600'},
                      {bg: 'bg-indigo-50', border: 'border-l-indigo-500', badge: 'bg-indigo-600'},
                      {bg: 'bg-orange-50', border: 'border-l-orange-500', badge: 'bg-orange-600'},
                      {bg: 'bg-cyan-50', border: 'border-l-cyan-500', badge: 'bg-cyan-600'},
                      {bg: 'bg-pink-50', border: 'border-l-pink-500', badge: 'bg-pink-600'},
                    ];
                    const c = colors[idx % colors.length];
                    return (
                    <tr key={idx} className={`${c.bg} border-l-4 ${c.border} hover:brightness-95 transition-all`}>
                      <td className="p-3 text-center border">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-xs ${c.badge}`}>
                          {week.week}
                        </span>
                      </td>
                      <td className="p-3 border font-semibold text-slate-800">{week.topic}</td>
                      <td className="p-3 border">
                        <ul className="list-none space-y-1">
                          {(week.learning_objectives || []).map((obj, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-slate-600">
                              <Target className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
                              <span>{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="p-3 border">
                        <ul className="list-none space-y-1">
                          {(week.activities || []).map((act, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-slate-600">
                              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
                              <span>{act}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="p-3 border text-slate-600 text-xs">{week.resources}</td>
                      <td className="p-3 border text-slate-600 text-xs">{week.assessment}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
